import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { grupoFamiliarApi } from '../../services/api'
import { Button, Modal, Tooltip } from '../ui'
import { confirmar } from '../../lib/confirmar'

// Hogar (grupo familiar) de la persona: quiénes viven juntos, el referente, el
// domicilio y qué recibe cada integrante. Una persona está en un solo hogar; los
// parentescos (madre de, hermano de…) se cargan aparte, en "Vínculos".
// Vínculo de El Niño → rol en el hogar, para sumar a un conviviente sugerido.
const ROL_DESDE_ELNINO = { 'hijo/a': 'HIJO/A', 'esposo/a': 'CONYUGE', 'concubino/a': 'CONYUGE', 'nieto/a': 'NIETO/A', 'hermano/a': 'HERMANO/A', 'sobrino/a': 'SOBRINO/A', 'abuelo/a': 'ABUELO/A', padre: 'PADRE/MADRE', madre: 'PADRE/MADRE' }
const RIESGO = { directa: 'bg-rose-100 text-rose-800', medio: 'bg-amber-100 text-amber-800', bajo: 'bg-slate-100 text-ink-soft' }
const fecha = (v) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '')

// Inundaciones y El Niño sobre el hogar: si alguien ya fue evacuado, cómo lo relevó
// El Niño y quién más vivía con ellos según ese relevamiento.
function Emergencias({ e, grupo, onSumar, onArmar, trabajando }) {
  if (!e || (!e.evacuaciones.length && !e.elnino.length)) return null
  return (
    <div className="space-y-3 border-t border-hairline bg-sky-50/40 px-6 py-4">
      <p className="text-xs font-black uppercase tracking-wide text-sky-900">🌊 Antecedentes de inundación {grupo ? 'del hogar' : ''}</p>
      {e.evacuaciones.map((v) => (
        <p key={`${v.persona_id}-${v.evento}`} className="text-sm text-ink">
          <b>{v.apellido}, {v.nombre}</b> fue evacuado/a en <b>{v.evento}</b> ({fecha(v.fecha)}) · {v.centro_evacuacion}
          {v.barrio_afectado ? ` · barrio ${v.barrio_afectado}` : ''}{v.cantidad_integrantes ? ` · ${v.cantidad_integrantes} integrantes` : ''}
        </p>
      ))}
      {e.elnino.map((r) => (
        <p key={`${r.persona_id}-${r.hogar_elnino_id}`} className="text-sm text-ink">
          <span className={`mr-1 rounded-full px-2 py-0.5 text-[11px] font-black uppercase ${RIESGO[r.nivel_riesgo] || RIESGO.bajo}`}>El Niño · riesgo {r.nivel_riesgo || 's/d'}</span>
          <b>{r.apellido}, {r.nombre}</b> relevado/a como {String(r.vinculo || 'integrante').toLowerCase()} el {fecha(r.fecha_relevamiento)}
          {r.integrantes_relevados ? ` · hogar de ${r.integrantes_relevados}` : ''}{r.evacuado ? ` · evacuado/a${r.lugar_evacuacion ? ` en ${r.lugar_evacuacion}` : ''}` : ''}
        </p>
      ))}
      {e.sugeridos.length > 0 && (
        <div className="rounded-xl bg-white p-3 ring-1 ring-sky-100">
          <p className="mb-2 text-xs font-bold text-sky-900">Según El Niño también viven acá{grupo ? ' (y no están en este hogar)' : ''}:</p>
          <div className="flex flex-wrap gap-2">
            {e.sugeridos.map((s) => (
              <span key={s.persona_id} className="inline-flex items-center gap-2 rounded-full bg-sky-50 py-1 pl-3 pr-1 text-xs font-semibold text-ink">
                <Link to={`/persona/${s.persona_id}`} className="hover:underline">{s.apellido}, {s.nombre}</Link>
                <span className="text-ink-muted">{s.vinculo || ''}{s.edad != null ? ` · ${s.edad} años` : ''}</span>
                {grupo && (
                  <button type="button" disabled={trabajando} onClick={() => onSumar(s)} title="Sumarlo a este hogar"
                    className="rounded-full bg-social-700 px-2 py-0.5 text-[11px] font-bold text-white hover:bg-social-800">+ Sumar</button>
                )}
              </span>
            ))}
          </div>
          {!grupo && onArmar && (
            <button type="button" disabled={trabajando} onClick={onArmar}
              className="mt-3 rounded-xl bg-social-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-social-800">
              Armar el hogar con esta persona como referente y sumarlos
            </button>
          )}
        </div>
      )}
      {e.sin_ficha.length > 0 && (
        <p className="text-xs text-ink-muted">
          El Niño relevó además a {e.sin_ficha.map((s) => `${s.apellido || ''}, ${s.nombre || ''}${s.dni ? ` (DNI ${s.dni})` : ' (sin DNI)'}`).join(' · ')}, que no tienen ficha en Acción Social.
        </p>
      )}
    </div>
  )
}

const ROLES = ['RESPONSABLE', 'CONYUGE', 'HIJO/A', 'NIETO/A', 'SOBRINO/A', 'HERMANO/A', 'PADRE/MADRE', 'ABUELO/A', 'A CARGO', 'INTEGRANTE']

export default function HogarPersona({ personaId, persona }) {
  const [grupo, setGrupo] = useState(undefined) // undefined = cargando, null = sin hogar
  const [trabajando, setTrabajando] = useState(false)
  const [nuevo, setNuevo] = useState({ dni: '', rol: 'HIJO/A' })
  const [editando, setEditando] = useState(null)
  const [emergencias, setEmergencias] = useState(null)

  const cargar = useCallback(async () => {
    try {
      const r = await grupoFamiliarApi.dePersona(personaId)
      setGrupo(r.data?.data?.grupo || null)
      setEmergencias(r.data?.data?.emergencias || null)
    } catch {
      setGrupo(null)
      toast.error('No se pudo cargar el grupo familiar')
    }
  }, [personaId])

  useEffect(() => { cargar() }, [cargar])

  const accion = async (fn, ok) => {
    try {
      setTrabajando(true)
      const r = await fn()
      if (r?.data?.data) setGrupo(r.data.data)
      if (ok) toast.success(ok)
      return true
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo guardar')
      return false
    } finally {
      setTrabajando(false)
    }
  }

  const agregar = async (e, mover = false) => {
    e?.preventDefault()
    if (String(nuevo.dni).replace(/\D/g, '').length < 6) return toast.error('Ingresá el DNI del integrante')
    try {
      setTrabajando(true)
      const r = await grupoFamiliarApi.agregarMiembro(grupo.id, { ...nuevo, mover })
      setGrupo(r.data.data)
      setNuevo({ dni: '', rol: nuevo.rol })
      toast.success('Integrante agregado al hogar')
    } catch (err) {
      const d = err?.response?.data
      if (err?.response?.status === 409 && d?.conflicto && await confirmar({ titulo: `¿Mudarlo a ${grupo.codigo}?`, mensaje: d.error, accion: 'Mudar al hogar', peligro: false })) {
        setTrabajando(false)
        return agregar(null, true)
      }
      toast.error(d?.error || 'No se pudo agregar')
    } finally {
      setTrabajando(false)
    }
  }

  const sumarSugerido = async (s) => {
    const rol = ROL_DESDE_ELNINO[String(s.vinculo || '').toLowerCase()] || 'INTEGRANTE'
    try {
      setTrabajando(true)
      await grupoFamiliarApi.agregarMiembro(grupo.id, { persona_id: s.persona_id, rol })
      toast.success(`${s.apellido}, ${s.nombre} sumado/a al hogar`)
    } catch (err) {
      const d = err?.response?.data
      if (err?.response?.status === 409 && d?.conflicto && await confirmar({ titulo: `¿Mudarlo a ${grupo.codigo}?`, mensaje: d.error, accion: 'Mudar al hogar', peligro: false })) {
        await grupoFamiliarApi.agregarMiembro(grupo.id, { persona_id: s.persona_id, rol, mover: true }).catch(() => toast.error('No se pudo mudar'))
      } else if (!d?.conflicto) toast.error(d?.error || 'No se pudo sumar')
    } finally {
      setTrabajando(false)
      cargar()
    }
  }

  // Sin hogar pero con convivientes relevados por El Niño: se arma el hogar de una vez.
  // Quien ya vive en otro hogar no se muda: queda avisado para decidirlo a mano.
  const armarDesdeElNino = async () => {
    try {
      setTrabajando(true)
      const g = (await grupoFamiliarApi.crear(personaId)).data.data
      let enOtro = 0
      for (const s of emergencias.sugeridos) {
        const rol = ROL_DESDE_ELNINO[String(s.vinculo || '').toLowerCase()] || 'INTEGRANTE'
        await grupoFamiliarApi.agregarMiembro(g.id, { persona_id: s.persona_id, rol }).catch(() => { enOtro += 1 })
      }
      toast.success(`Hogar ${g.codigo} armado`)
      if (enOtro) toast(`${enOtro} ya vivían en otro hogar y no se movieron`, { icon: '⚠️', duration: 7000 })
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo armar el hogar')
    } finally {
      setTrabajando(false)
      cargar()
    }
  }

  if (grupo === undefined) return <div className="h-24 animate-pulse rounded-3xl bg-slate-100" />

  if (!grupo) {
    return (
      <div className="overflow-hidden rounded-3xl border-2 border-dashed border-violet-200">
      <div className="flex flex-col items-start justify-between gap-3 bg-violet-50/40 p-5 sm:flex-row sm:items-center">
        <div>
          <p className="font-black text-violet-900">👨‍👩‍👧 Sin hogar registrado</p>
          <p className="text-sm text-violet-800/80">
            El hogar agrupa a quienes viven juntos bajo un código (GF-000145) con un referente y un domicilio. Se arma solo al
            inscribir un chico en un comedor con su tutor, o al agregar un familiar que convive.
          </p>
        </div>
        <Tooltip content={`Crea el hogar con ${persona?.apellido || 'esta persona'} como referente, usando su domicilio, barrio y teléfono.`}>
          <Button variant="social" loading={trabajando} onClick={() => accion(() => grupoFamiliarApi.crear(personaId), 'Hogar creado').then((ok) => ok && cargar())}>Crear hogar</Button>
        </Tooltip>
      </div>
      <Emergencias e={emergencias} grupo={null} onArmar={armarDesdeElNino} trabajando={trabajando} />
      </div>
    )
  }

  const chicos = grupo.miembros.filter((m) => m.edad != null && m.edad < 18).length
  const enComedor = grupo.miembros.filter((m) => m.comedores.length).length

  return (
    <div className="overflow-hidden rounded-3xl border border-violet-100 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-violet-50 via-white to-social-50 px-6 py-4">
        <div>
          <p className="font-mono text-xs font-black tracking-wider text-violet-700">HOGAR {grupo.codigo}</p>
          <p className="text-lg font-black text-ink">
            {grupo.referente_apellido ? `Referente: ${grupo.referente_apellido}, ${grupo.referente_nombre}` : 'Sin referente'}
          </p>
          <p className="text-sm text-ink-soft">
            {[grupo.domicilio, grupo.barrio, grupo.telefono && `☎ ${grupo.telefono}`].filter(Boolean).join(' · ') || 'Sin domicilio cargado'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs font-bold">
          <span className="rounded-full bg-violet-100 px-3 py-1 text-violet-800">{grupo.miembros.length} integrantes</span>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-ink-soft">{chicos} menores</span>
          {enComedor > 0 && <span className="rounded-full bg-social-100 px-3 py-1 text-social-800">🍽️ {enComedor} en comedor</span>}
          <Tooltip content="Domicilio, barrio, teléfono y referente del hogar">
            <button type="button" onClick={() => setEditando({ domicilio: grupo.domicilio || '', barrio: grupo.barrio || '', telefono: grupo.telefono || '', observaciones: grupo.observaciones || '', referente_persona_id: grupo.referente_persona_id || '' })}
              className="rounded-lg px-2 py-1 text-brand-primary hover:bg-slate-100">Editar hogar</button>
          </Tooltip>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-ink-soft">
            <tr>
              <th className="px-6 py-2">Integrante</th><th className="px-4 py-2">Rol en el hogar</th><th className="px-4 py-2">Edad</th>
              <th className="px-4 py-2"><Tooltip side="bottom" content="Comedor donde está inscripto y días que vino en los últimos 30 sobre los días con servicio."><span tabIndex={0} className="cursor-help">Comedor · asistencia ⓘ</span></Tooltip></th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {grupo.miembros.map((m) => (
              <tr key={m.persona_id} className={m.persona_id === personaId ? 'bg-violet-50/50' : 'hover:bg-slate-50'}>
                <td className="px-6 py-2.5">
                  {m.persona_id === personaId
                    ? <span className="font-bold text-ink">{m.apellido}, {m.nombre} <span className="text-xs text-violet-700">(esta ficha)</span></span>
                    : <Link to={`/persona/${m.persona_id}`} className="font-bold text-ink hover:text-social-700 hover:underline">{m.apellido}, {m.nombre}</Link>}
                  <span className="block text-xs text-ink-muted">DNI {m.dni}</span>
                </td>
                <td className="px-4 py-2.5 text-xs font-black uppercase text-violet-700">{m.persona_id === grupo.referente_persona_id ? '★ REFERENTE' : (m.rol || '—')}</td>
                <td className="px-4 py-2.5 tabular-nums">{m.edad ?? '—'}</td>
                <td className="px-4 py-2.5">
                  {m.comedores.length === 0 ? <span className="text-xs text-ink-muted">—</span> : m.comedores.map((c) => (
                    <span key={c.sede} className="block text-xs">
                      🍽️ <b>{c.sede}</b>{c.asistencia_pct != null ? ` · ${c.asistencia_pct}%` : ' · sin asistencia'}
                    </span>
                  ))}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button type="button" disabled={trabajando}
                    onClick={async () => {
                      if (!(await confirmar({ titulo: `¿Sacar a ${m.apellido}, ${m.nombre} del hogar?`, mensaje: `Sale del hogar ${grupo.codigo}. No se borra la persona.`, accion: 'Sacar del hogar' }))) return
                      await accion(() => grupoFamiliarApi.quitarMiembro(grupo.id, m.persona_id), 'Quitado del hogar')
                      if (m.persona_id === personaId) cargar()
                    }}
                    title="Sacar del hogar (la persona no se borra)"
                    className="rounded-lg px-2 py-1 text-xs font-bold text-danger-dark hover:bg-danger-soft">Quitar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Emergencias e={emergencias} grupo={grupo} onSumar={sumarSugerido} trabajando={trabajando} />

      <form onSubmit={agregar} className="flex flex-wrap items-end gap-2 border-t border-hairline bg-slate-50/60 px-6 py-3">
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase text-ink-soft">Agregar integrante (DNI)</span>
          <input className="input w-40 py-1.5" inputMode="numeric" value={nuevo.dni} onChange={(e) => setNuevo((n) => ({ ...n, dni: e.target.value }))} placeholder="DNI" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase text-ink-soft">Rol</span>
          <select className="input w-40 py-1.5" value={nuevo.rol} onChange={(e) => setNuevo((n) => ({ ...n, rol: e.target.value }))}>
            {ROLES.map((r) => <option key={r}>{r}</option>)}
          </select>
        </label>
        <Tooltip content="La persona tiene que estar cargada en el sistema. Si vive en otro hogar, te pregunta antes de mudarla.">
          <Button type="submit" variant="social" size="sm" loading={trabajando}>Agregar al hogar</Button>
        </Tooltip>
      </form>

      <Modal isOpen={!!editando} onClose={() => setEditando(null)} title={`Editar hogar ${grupo.codigo}`} size="md">
        {editando && (
          <form className="space-y-3" onSubmit={async (e) => {
            e.preventDefault()
            if (await accion(() => grupoFamiliarApi.actualizar(grupo.id, editando), 'Hogar actualizado')) setEditando(null)
          }}>
            {[['domicilio', 'Domicilio'], ['barrio', 'Barrio / paraje'], ['telefono', 'Teléfono de contacto'], ['observaciones', 'Observaciones']].map(([k, l]) => (
              <label key={k} className="block">
                <span className="mb-1 block text-xs font-bold uppercase text-ink-soft">{l}</span>
                <input className="input" value={editando[k]} onChange={(e) => setEditando((x) => ({ ...x, [k]: e.target.value }))} />
              </label>
            ))}
            <label className="block">
              <span className="mb-1 block text-xs font-bold uppercase text-ink-soft">Referente</span>
              <select className="input" value={editando.referente_persona_id} onChange={(e) => setEditando((x) => ({ ...x, referente_persona_id: e.target.value }))}>
                <option value="">— sin referente —</option>
                {grupo.miembros.filter((m) => m.edad == null || m.edad >= 18).map((m) => <option key={m.persona_id} value={m.persona_id}>{m.apellido}, {m.nombre}</option>)}
              </select>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setEditando(null)}>Cancelar</Button>
              <Button type="submit" variant="social" loading={trabajando}>Guardar</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
